import config from '../utils/config';

class PKCEAuthService {
  constructor() {
    this.baseURL = config.API_BASE_URL;
  }

  async generatePKCEChallenge() {
    const codeVerifier = this.base64URLEncode(crypto.getRandomValues(new Uint8Array(32)));
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    const digest = await crypto.subtle.digest('SHA-256', data);
    const codeChallenge = this.base64URLEncode(new Uint8Array(digest));
    return { codeVerifier, codeChallenge };
  }

  getClientId() {
    const clientId = localStorage.getItem('welliemd_client_id');
    
    if (!clientId) {
      throw new Error('No client ID found. Please ensure URL contains client ID.');
    }
    
    return clientId;
  }

  base64URLEncode(buffer) {
    return btoa(String.fromCharCode.apply(null, buffer))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=/g, '');
  }

  async initializePKCEFlow(templateId, sessionIdentifier, redirectUri) {
    const { codeVerifier, codeChallenge } = await this.generatePKCEChallenge();
    
    const response = await fetch(`${this.baseURL}/questionnaires/oauth/initialize/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-ID': this.getClientId()
      },
      body: JSON.stringify({
        template_id: templateId,
        session_identifier: sessionIdentifier,
        redirect_uri: redirectUri,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256'
      })
    });

    if (!response.ok) {
      throw new Error(`PKCE initialization failed: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.success) {
      sessionStorage.setItem('pkce_state', data.data.state);
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      sessionStorage.setItem('pkce_code_challenge', codeChallenge);
      
      return {
        ...data.data,
        codeVerifier
      };
    }
    
    throw new Error(data.message || 'PKCE initialization failed');
  }

  async authorizeAccess(state, codeChallenge, redirectUri, templateId) {
    const clientId = this.getClientId();
    
    const response = await fetch(
      `${this.baseURL}/questionnaires/oauth/authorize/?` + 
      `client_id=${clientId}&` +
      `state=${state}&` +
      `code_challenge=${codeChallenge}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      `template_id=${templateId}&` +
      `response_type=code&` +
      `code_challenge_method=S256`,
      {
        method: 'GET',
        headers: {
          'X-Client-ID': clientId,
          'Origin': window.location.origin
        }
      }
    );
  
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Authorization failed: ${errorData.message || response.status}`);
    }
  
    const data = await response.json();
    
    if (data.success) {
      return data.authorization_code;
    }
    
    throw new Error(data.message || 'Authorization failed');
  }
  
  async exchangeCodeForToken(authCode, state, redirectUri) {
    const storedCodeVerifier = sessionStorage.getItem('pkce_code_verifier');
    
    if (!storedCodeVerifier) {
      throw new Error('Code verifier not found in session storage');
    }
    
    const response = await fetch(`${this.baseURL}/questionnaires/oauth/token/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-ID': this.getClientId()
      },
      body: JSON.stringify({
        code: authCode,
        code_verifier: storedCodeVerifier,
        state: state,
        redirect_uri: redirectUri,
        grant_type: 'authorization_code'
      })
    });
  
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Token exchange failed: ${errorData.message || response.status}`);
    }
  
    const data = await response.json();
    
    if (data.success) {
      const tokenData = data.data;
      
      localStorage.setItem('welliemd_session_token', tokenData.questionnaire_session.session_token);
      localStorage.setItem('welliemd_session_expires', Date.now() + (tokenData.expires_in * 1000));
      localStorage.setItem('welliemd_client_id', this.getClientId());
      
      sessionStorage.removeItem('pkce_state');
      sessionStorage.removeItem('pkce_code_verifier');
      sessionStorage.removeItem('pkce_code_challenge');
      
      return tokenData;
    }
    
    throw new Error(data.message || 'Token exchange failed');
  }
  
  async authenticateWithPKCE(templateId) {
    const redirectUri = window.location.origin + '/callback';
    const sessionIdentifier = `anonymous_${Date.now()}_${Math.random()}`;
    
    const pkceData = await this.initializePKCEFlow(templateId, sessionIdentifier, redirectUri);
    const authCode = await this.authorizeAccess(pkceData.state, pkceData.code_challenge, redirectUri, templateId);
    const tokenData = await this.exchangeCodeForToken(authCode, pkceData.state, redirectUri);
    
    return tokenData;
  }

  getSessionToken() {
    const token = localStorage.getItem('welliemd_session_token');
    const expires = localStorage.getItem('welliemd_session_expires');
    
    if (!token || !expires) return null;
    
    if (Date.now() >= parseInt(expires)) {
      this.logout();
      return null;
    }
    
    return token;
  }

  isAuthenticated() {
    return !!this.getSessionToken();
  }

  logout() {
    localStorage.removeItem('welliemd_session_token');
    localStorage.removeItem('welliemd_session_expires');
    localStorage.removeItem('welliemd_client_id');
    sessionStorage.removeItem('pkce_state');
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('pkce_code_challenge');
  }
}

export default new PKCEAuthService();
